import { PERMISOS_REPORTES } from '@mg/shared';
import { Navigate, Outlet, Route, Routes } from 'react-router';
import { AyudaFlotante } from './ayuda/BotonAyuda.tsx';
import { AyudaProvider } from './ayuda/contexto.tsx';
import { PaginaAyuda } from './ayuda/PaginaAyuda.tsx';
import { AppLayout } from './layout/AppLayout.tsx';
import { useAuth } from './lib/auth.tsx';
import { Auditoria } from './paginas/Auditoria.tsx';
import { Autorizaciones } from './paginas/Autorizaciones.tsx';
import { Caja } from './paginas/Caja.tsx';
import { Configuracion } from './paginas/Configuracion.tsx';
import { Consolidado } from './paginas/Consolidado.tsx';
import { Login } from './paginas/Login.tsx';
import { ProductoFicha, Productos } from './modulos/productos/Productos.tsx';
import { Combos } from './modulos/combos/Combos.tsx';
import { ClienteFicha, Clientes } from './modulos/clientes/Clientes.tsx';
import { POS } from './modulos/ventas/POS.tsx';
import { DocumentoDetalle, Documentos } from './modulos/ventas/Documentos.tsx';
import { HojaDePrueba } from './modulos/ventas/HojaDePrueba.tsx';
import { Imprimir } from './modulos/ventas/Imprimir.tsx';
import { Entregas, ImprimirConstanciaPendiente, ImprimirEntrega } from './modulos/inventario/Entregas.tsx';
import { Inventario } from './modulos/inventario/Inventario.tsx';
import { Ajustes } from './modulos/inventario/Ajustes.tsx';
import { Roturas } from './modulos/inventario/Roturas.tsx';
import { ImprimirRetiro } from './modulos/caja/Retiros.tsx';
import { Cierres, ImprimirCierre } from './modulos/cierres/Cierres.tsx';
import { Cotizaciones, DetalleCotizacion } from './modulos/cotizaciones/Cotizaciones.tsx';
import { EditorCotizacion } from './modulos/cotizaciones/EditorCotizacion.tsx';
import { ImprimirCotizacion } from './modulos/cotizaciones/ImprimirCotizacion.tsx';
import { ImprimirAbono } from './modulos/ventas/ImprimirAbono.tsx';
import { Devoluciones } from './modulos/devoluciones/Devoluciones.tsx';
import { Compras } from './modulos/compras/Compras.tsx';
import { PagosRecibidos } from './modulos/ventas/PagosRecibidos.tsx';
import { PaginaGastos } from './modulos/gastos/Gastos.tsx';
import { EditorCompra } from './modulos/compras/EditorCompra.tsx';
import { ImprimirComprobante } from './modulos/compras/ImprimirComprobante.tsx';
import { EditorOrdenCompra } from './modulos/compras/EditorOrdenCompra.tsx';
import { ImprimirOrdenCompra } from './modulos/compras/ImprimirOrdenCompra.tsx';
import { Libros } from './modulos/reportes/Libros.tsx';
import { ImprimirReporteIgtf } from './modulos/reportes/ImprimirIgtf.tsx';
import { Reportes } from './modulos/reportes/Reportes.tsx';
import { Retenciones } from './modulos/retenciones/Retenciones.tsx';
import { Flota } from './modulos/flota/Flota.tsx';
import { ImprimirTraslado, Traslados } from './modulos/traslados/Traslados.tsx';
import { Panel } from './paginas/Panel.tsx';
import { NoEncontrado } from './paginas/Pendiente.tsx';
import { GuardaPermiso, RutaPrivada } from './rutas/guardas.tsx';

/** El superadmin sin contexto vive en /consolidado; con contexto opera como cualquier sucursal. */
function Inicio() {
  const { usuario } = useAuth();
  if (usuario?.rol === 'superadmin' && !usuario.sucursal_id) return <Navigate to="/consolidado" replace />;
  return <Panel />;
}

/** Las pantallas de impresión: el formato y, fuera del papel, el botón de ayuda. */
function LayoutImpresion() {
  return (
    <>
      <Outlet />
      <AyudaFlotante tema="impresion" />
    </>
  );
}

export function App() {
  return (
    <AyudaProvider>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route element={<RutaPrivada />}>
        <Route element={<LayoutImpresion />}>
        <Route path="imprimir/ENT/:id" element={<ImprimirEntrega />} />
        <Route path="imprimir/PEN/:id" element={<ImprimirConstanciaPendiente />} />
        <Route path="imprimir/CIERRE/:id" element={<ImprimirCierre />} />
        <Route path="imprimir/RETC/:id" element={<ImprimirRetiro />} />
        <Route path="imprimir/COT/:id" element={<ImprimirCotizacion />} />
        <Route path="imprimir/ABONO/:id" element={<ImprimirAbono />} />
        <Route path="imprimir/TRAS/:id" element={<ImprimirTraslado />} />
        <Route path="imprimir/COMPROBANTE/:id/:cid" element={<ImprimirComprobante />} />
        <Route path="imprimir/OC/:id" element={<ImprimirOrdenCompra />} />
        <Route path="imprimir/PRUEBA/:sucursalId" element={<HojaDePrueba />} />
        <Route path="imprimir/REPORTE/igtf" element={<ImprimirReporteIgtf />} />
        <Route path="imprimir/:tipo/:id" element={<Imprimir />} />
        </Route>
        <Route element={<AppLayout />}>
          <Route index element={<Inicio />} />
          <Route path="ayuda" element={<PaginaAyuda />} />
          <Route path="caja" element={<GuardaPermiso permiso="caja.abrir"><Caja /></GuardaPermiso>} />
          {/* Pide lo mismo que su menú y su tablero (re-auditoría 2026-10-18, ronda 2, hallazgo 7-H8); el selector de contexto sigue siendo solo de la dueña */}
          <Route path="consolidado" element={<GuardaPermiso permiso="reportes.consolidado"><Consolidado /></GuardaPermiso>} />
          <Route path="configuracion" element={<GuardaPermiso permiso="usuarios.admin" nivel="total"><Configuracion /></GuardaPermiso>} />
          <Route path="auditoria" element={<GuardaPermiso permiso="auditoria.ver"><Auditoria /></GuardaPermiso>} />
          <Route path="autorizaciones" element={<GuardaPermiso permiso="autorizaciones.resolver" nivel="total"><Autorizaciones /></GuardaPermiso>} />
          <Route path="pos" element={<GuardaPermiso permiso="ventas.emitir" nivel="total"><POS /></GuardaPermiso>} />
          <Route path="cotizaciones" element={<GuardaPermiso permiso="cotizaciones.crear"><Cotizaciones /></GuardaPermiso>} />
          <Route path="cotizaciones/nueva" element={<GuardaPermiso permiso="cotizaciones.crear"><EditorCotizacion /></GuardaPermiso>} />
          <Route path="cotizaciones/:id" element={<GuardaPermiso permiso="cotizaciones.crear"><DetalleCotizacion /></GuardaPermiso>} />
          <Route path="cotizaciones/:id/editar" element={<GuardaPermiso permiso="cotizaciones.crear"><EditorCotizacion /></GuardaPermiso>} />
          <Route path="documentos" element={<GuardaPermiso permiso="ventas.emitir"><Documentos /></GuardaPermiso>} />
          <Route path="documentos/:id" element={<GuardaPermiso permiso="ventas.emitir"><DocumentoDetalle /></GuardaPermiso>} />
          <Route path="entregas" element={<GuardaPermiso permiso="inventario.entregas"><Entregas /></GuardaPermiso>} />
          <Route path="devoluciones" element={<GuardaPermiso permiso="devoluciones.registrar"><Devoluciones /></GuardaPermiso>} />
          <Route path="productos" element={<GuardaPermiso permiso="productos.consultar"><Productos /></GuardaPermiso>} />
          <Route path="productos/combos" element={<GuardaPermiso permiso="productos.consultar"><Combos /></GuardaPermiso>} />
          <Route path="productos/:id" element={<GuardaPermiso permiso="productos.consultar"><ProductoFicha /></GuardaPermiso>} />
          <Route path="inventario" element={<GuardaPermiso permiso="inventario.conteos" nivel="propone"><Inventario /></GuardaPermiso>} />
          <Route path="ajustes" element={<GuardaPermiso permiso="inventario.ajustes" nivel="propone"><Ajustes /></GuardaPermiso>} />
          <Route path="roturas" element={<GuardaPermiso permiso="inventario.reportar"><Roturas /></GuardaPermiso>} />
          <Route path="traslados" element={<GuardaPermiso permiso="traslados.despachar"><Traslados /></GuardaPermiso>} />
          <Route path="flota" element={<GuardaPermiso permiso="flota.gestionar"><Flota /></GuardaPermiso>} />
          <Route path="compras" element={<GuardaPermiso permiso="compras.registrar"><Compras /></GuardaPermiso>} />
          <Route path="compras/nueva" element={<GuardaPermiso permiso="compras.registrar"><EditorCompra /></GuardaPermiso>} />
          <Route path="compras/:id/editar" element={<GuardaPermiso permiso="compras.registrar"><EditorCompra /></GuardaPermiso>} />
          <Route path="compras/ordenes/nueva" element={<GuardaPermiso permiso="compras.registrar"><EditorOrdenCompra /></GuardaPermiso>} />
          <Route path="compras/ordenes/:id" element={<GuardaPermiso permiso="compras.registrar"><EditorOrdenCompra /></GuardaPermiso>} />
          <Route path="pagos-recibidos" element={<GuardaPermiso permiso="pagos.precargar"><PagosRecibidos /></GuardaPermiso>} />
          <Route path="gastos" element={<GuardaPermiso permiso="gastos.registrar"><PaginaGastos /></GuardaPermiso>} />
          <Route path="retenciones" element={<GuardaPermiso permiso="retenciones-clientes.cargar"><Retenciones /></GuardaPermiso>} />
          <Route path="clientes" element={<GuardaPermiso permiso="clientes.crear"><Clientes /></GuardaPermiso>} />
          <Route path="clientes/:id" element={<GuardaPermiso permiso="clientes.crear"><ClienteFicha /></GuardaPermiso>} />
          <Route path="cierres" element={<GuardaPermiso permiso="caja.abrir"><Cierres /></GuardaPermiso>} />
          {/* Con cualquier permiso de reportes: cada pestaña pide el suyo (hallazgo 6-H11) */}
          <Route path="reportes" element={<GuardaPermiso permiso={PERMISOS_REPORTES}><Reportes /></GuardaPermiso>} />
          <Route path="libros" element={<GuardaPermiso permiso="libros.ver"><Libros /></GuardaPermiso>} />
          <Route path="*" element={<NoEncontrado />} />
        </Route>
      </Route>
    </Routes>
    </AyudaProvider>
  );
}
